import { ArrowRight, Star, Sparkles } from "lucide-react";
import CountUp from "./CountUp";
import GrowthChart from "./GrowthChart";

export default function Hero({ onPrimary }) {
  return (
    <section id="top" className="relative isolate overflow-hidden" data-testid="hero-section">
      <div className="hero-glow" aria-hidden />
      <div className="absolute inset-0 grid-pattern" aria-hidden />
      <div className="relative mx-auto max-w-6xl px-5 pb-14 pt-14 md:px-6 md:pb-20 md:pt-20">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          {/* LEFT */}
          <div className="lg:col-span-7">
            <div
              className="animate-fade-up inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-blue-300"
              data-testid="hero-badge"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-pulse-ring" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-400" />
              </span>
              New · Performance Marketing Studio
            </div>

            <h1
              className="animate-fade-up delay-1 mt-6 font-display text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl lg:text-6xl"
              data-testid="hero-title"
            >
              Scale Your Business with{" "}
              <span className="gradient-text">Proven Marketing</span>
            </h1>

            <p
              className="animate-fade-up delay-3 mt-5 max-w-xl text-sm text-slate-400 sm:text-base"
              data-testid="hero-subtitle"
            >
              We help businesses generate leads, increase sales, and build a strong online presence.
            </p>

            <div className="animate-fade-up delay-4 mt-8 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onPrimary}
                className="btn-primary inline-flex h-12 items-center gap-2 rounded-full px-6 text-sm font-semibold text-white"
                data-testid="hero-get-started-btn"
              >
                Get Started <ArrowRight className="h-4 w-4" />
              </button>
              <a
                href="#pricing"
                className="btn-ghost inline-flex h-12 items-center gap-2 rounded-full px-6 text-sm font-semibold text-white"
                data-testid="hero-view-pricing-btn"
              >
                View Pricing
              </a>
            </div>

            {/* Trust badge */}
            <div
              className="animate-fade-up delay-5 mt-8 flex items-center gap-4"
              data-testid="hero-trust-badge"
            >
              <div className="flex -space-x-2">
                {[
                  "https://images.unsplash.com/photo-1762522926157-bcc04bf0b10a?auto=format&fit=crop&w=80&q=80",
                  "https://images.unsplash.com/photo-1576558656222-ba66febe3dec?auto=format&fit=crop&w=80&q=80",
                  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=80&q=80",
                  "https://images.unsplash.com/photo-1607746882042-944635dfe10e?auto=format&fit=crop&w=80&q=80",
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    className="h-9 w-9 rounded-full border-2 border-slate-950 object-cover"
                  />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-amber-400">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                  <span className="ml-1 text-sm font-semibold text-white">4.9/5</span>
                </div>
                <p className="text-xs text-slate-400">From 200+ happy clients</p>
              </div>
            </div>
          </div>

          {/* RIGHT – glass floating dashboard card */}
          <div className="relative lg:col-span-5">
            <div className="relative mx-auto max-w-md animate-fade-in delay-2">
              {/* radial accent behind */}
              <div
                className="absolute -inset-10 -z-10 rounded-full bg-blue-600/25 blur-3xl"
                aria-hidden
              />

              <div className="glass animate-float relative overflow-hidden rounded-3xl p-5 glow-blue">
                <GrowthChart />

                {/* Customers served counter */}
                <div
                  className="mt-4 relative overflow-hidden rounded-2xl border border-blue-500/25 bg-gradient-to-br from-blue-600/15 via-slate-900/30 to-indigo-600/15 p-4"
                  data-testid="customers-served-counter"
                >
                  <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-blue-500/30 blur-2xl" aria-hidden />
                  <div className="relative flex items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-blue-300">
                        <Sparkles className="h-3.5 w-3.5" />
                        Clients onboarded
                      </div>
                      <div className="mt-1 flex items-baseline gap-1">
                        <CountUp
                          end={1300}
                          suffix="+"
                          className="font-display text-3xl font-extrabold text-white"
                          testId="customers-served-count"
                        />
                        <span className="text-xs text-slate-400">and counting</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-emerald-400">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-pulse-ring" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                      </span>
                      <span className="text-xs font-semibold">Live</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
